import {
	getGetOpportunityByIdQueryKey,
	getGetOpportunityByIdQueryOptions,
} from "@fp/admin-api-client/client";
import { AdminPageHeader } from "@/components/admin-page-header";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
	PageContentContainer,
	PageLoading,
	PageProgressBar,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import {
	ParticipationForm,
	ParticipationFormContent,
	ParticipationFormErrorMessage,
	type ParticipationFormContext,
} from "@fp/ui/components/opportunities/opportunity-participation-form";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface ParticipationPageProps {
	opportunity: OpportunityDto;
	onSuccess: (opportunity: OpportunityDto) => Promise<void>;
}

export const ParticipationPageContent = ({
	opportunity,
	onSuccess,
}: ParticipationPageProps) => {
	const defaultValues = useMemo(
		() => ({
			id: opportunity.id,
			hasParticipationLimit: (opportunity.maxClaimsPerRoster ?? 0) > 0,
			maxClaimsPerRoster: opportunity.maxClaimsPerRoster?.toString() ?? "",
		}),
		[opportunity],
	);

	return (
		<ParticipationForm defaultValues={defaultValues} onSuccess={onSuccess}>
			{({ handleSubmit, transition }: ParticipationFormContext) => (
				<>
					<ParticipationFormContent />
					<ParticipationFormErrorMessage />

					<div className="flex-1 flex items-end pt-6">
						<Button
							isDisabled={transition}
							type="button-primary"
							onClick={handleSubmit}
							className="h-16 w-full"
						>
							Set Proof of Work
						</Button>
					</div>
				</>
			)}
		</ParticipationForm>
	);
};

export const Route = createFileRoute("/opps/$oppId/edit/participation")({
	component: RouteComponent,
	loader: async ({ params, context: { queryClient } }) => {
		const opportunity = await queryClient.fetchQuery(
			getGetOpportunityByIdQueryOptions(params.oppId),
		);
		return opportunity.data;
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const opportunity = Route.useLoaderData();
	const router = useRouter();
	const { oppId } = Route.useParams();
	const queryClient = useQueryClient();

	const onSuccess = async (opportunity: OpportunityDto) => {
		await queryClient.invalidateQueries({
			queryKey: getGetOpportunityByIdQueryKey(opportunity.id),
		});
		await router.invalidate({ sync: true });
		router.navigate({
			to: "/opps/$oppId/edit/proof-of-work",
			params: { oppId: opportunity.id },
		});
	};

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Participation Settings"
				parent={{
					url: router.buildLocation({
						to: "/opps/$oppId/edit/set-rates",
						params: { oppId },
					}).href,
					label: "Set Pay Rate",
				}}
				footer={<PageProgressBar current={2} total={4} />}
			/>
			<PageContentContainer className="space-y-6">
				<ParticipationPageContent
					opportunity={opportunity}
					onSuccess={onSuccess}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}
